import { useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CheckboxStandalone,
  Column,
  Columns,
  Heading,
  Inline,
  List,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import React, { useState } from 'react';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import { Error as GenericError } from 'src/components/Error/Error';
import { getConfigValues } from 'src/config';
import Routes from 'src/config/routes';
import {
  ListingProductCode,
  PurchaseFreemiumDocument,
} from 'src/generated/graphql';
import { fireListingCoreProductPurchasesToHeap } from 'src/utils/analytics';

import { PaymentContainer } from '../PaymentContainer';

export const FreemiumContract = () => {
  const [checked, setChecked] = useState<boolean>(false);
  const [displayErrorMessage, setDisplayErrorMessage] =
    useState<boolean>(false);

  const [purchaseFreemium, { loading, error }] = useMutation(
    PurchaseFreemiumDocument,
  );

  const onSubmit = async () => {
    if (!checked) {
      setDisplayErrorMessage(true);
      return;
    }

    try {
      await purchaseFreemium();
    } catch {
      // Do nothing, we'll handle via the mutation's error
      return;
    }

    // This is so that we can track conversions of Freemium clients the same way as others
    fireListingCoreProductPurchasesToHeap(ListingProductCode.Freemium);

    const casbaUrl =
      getConfigValues().ADVERTISER_CENTRE_URL + Routes.CREATE_AD_URL;
    window.location.assign(casbaUrl);
  };

  if (loading) {
    return (
      <PaymentContainer>
        <Box>
          <CustomLoader />
        </Box>
      </PaymentContainer>
    );
  }

  if (error) {
    return (
      <PaymentContainer>
        <GenericError />
      </PaymentContainer>
    );
  }

  return (
    <PaymentContainer>
      <Card>
        <Stack space="gutter">
          <Box paddingBottom="medium">
            <Heading level="2" weight="weak">
              Review and accept Free listing terms
            </Heading>
          </Box>
          <Text>
            Before creating your Free business listing, please review and accept
            these terms:
          </Text>

          <List>
            <Text>
              “Free” means you won&apos;t be charged for posting your ad on the
              SEEK Business website.
            </Text>
            <Text>
              You can only have one Free listing on SEEK Business at a time.
            </Text>
            <Text>
              {' '}
              Your Free listing will stay on the site for up to 6 months, but
              you can remove it any time.
            </Text>
            <Text>
              {' '}
              Free listings appear lower in search results than paid listings
              and have a different appearance.
            </Text>
            <Text>
              If you want to upgrade to a paid listing later, you&apos;ll need
              to create a new listing.
            </Text>
            <Text>
              Free listings are only available for individuals selling a
              business they own, not for third parties assisting with the sale.
            </Text>
          </List>

          <Text>
            For any further questions, please email us at{' '}
            <TextLink weight="weak" href="mailto:enquiries@seekbusiness.com.au">
              enquiries@seekbusiness.com.au
            </TextLink>
            .
          </Text>

          <Columns space="gutter">
            <Column width="content">
              <CheckboxStandalone
                id="contractCheckbox"
                aria-label="contractCheckbox"
                data={{ testid: 'freemium-contract-checkbox' }}
                checked={checked}
                onChange={() => {
                  setChecked(!checked);
                  setDisplayErrorMessage(false);
                }}
                tone={displayErrorMessage ? 'critical' : 'neutral'}
              />
            </Column>
            <Column>
              <Stack space="small">
                <Stack space="medium">
                  <Text>
                    You warrant that you have the legal authority to enter into
                    this agreement on behalf of your organisation and
                    acknowledge and agree that you have read and understood all
                    the{' '}
                    <TextLink
                      href="https://www.seekbusiness.com.au/terms-and-conditions"
                      target="_blank"
                    >
                      Terms and Conditions
                    </TextLink>{' '}
                    of this agreement (including the{' '}
                    <TextLink
                      href="https://www.seekbusiness.com.au/contract-terms"
                      target="_blank"
                    >
                      Advertising Terms
                    </TextLink>
                    ) and are aware that the terms contain limitations on SEEK’s
                    liability.
                  </Text>
                  <Text>
                    By continuing, you are entering into an agreement with SEEK
                    and will be bound by the terms and conditions contained
                    herein.
                  </Text>
                </Stack>
                {displayErrorMessage && (
                  <Text tone="critical">
                    <i>
                      You must tick the checkbox in order to accept these terms.
                    </i>
                  </Text>
                )}
              </Stack>
            </Column>
          </Columns>

          <Inline space="small">
            <Button
              // Realistically we shouldn't see this button when loading due to
              // the above `if (loading)` check but I'm putting this here just in case
              loading={loading}
              type="submit"
              variant={checked ? 'solid' : 'ghost'}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              data={{
                htag: 'freemium-accept-cta',
                analytics: 'freemium-accept-cta',
              }}
            >
              Accept terms and continue
            </Button>
          </Inline>
        </Stack>
      </Card>
    </PaymentContainer>
  );
};
